import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { InstitutionProvider } from "./contexts/InstitutionDataContext";
import { motion, AnimatePresence } from 'framer-motion';
import FloatingBackButton from './components/FloatingBackButton';
import Modal from './components/Modal';
import LoadingOverlay from './components/LoadingOverlay';
import ThemeHandler from './components/ThemeHandler';
import GlobalBackground from './components/GlobalBackground';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Gestiona la posición del scroll al cambiar de ruta.
 *
 * Antes esta lógica forzaba el scroll a 0 en CADA navegación, así que al volver
 * desde una clase a la lista se perdía el sitio.
 *
 * Ahora se restaura en dos casos:
 *  - `POP`: el usuario está volviendo de verdad. Cubre el botón atrás de
 *    Android, el gesto de deslizar en iOS y los `navigate(-1)` de la app.
 *  - `location.state.restoreScroll`: el botón "← Volver" de la app lo pide
 *    explícitamente. Varias páginas vuelven con `navigate('/ruta')`, que es una
 *    navegación NUEVA (PUSH) y no un atrás, así que el navegador no puede
 *    deducirlo: hay que declararlo.
 *
 * En cualquier otro caso se va arriba, que es el comportamiento de siempre.
 */
function ScrollManager() {
  const location = useLocation();
  const { pathname, state } = location;
  const navigationType = useNavigationType();
  const { user } = useAuth();

  // Posición por ruta. Vive en memoria a propósito: es estado de interfaz, no
  // un dato de la aplicación, y no debe sobrevivir a la sesión.
  const posiciones = useRef(new Map());
  const rutaActual = useRef(pathname);

  // Se captura MIENTRAS el usuario se desplaza, no al navegar: cuando la ruta
  // cambia, el contenido ya cambió y el navegador pudo haber recortado el
  // scroll, así que leerlo entonces daría un valor equivocado.
  //
  // El listener va en `window` CON CAPTURA. Los eventos `scroll` no burbujean,
  // pero sí tienen fase de captura, así que así se recogen también los del
  // contenedor. Eso evita guardar una referencia al elemento: al montar (app
  // recién abierta, en /login) `.dashboard-content` todavía no existe, y una
  // referencia tomada entonces quedaría nula para siempre.
  useEffect(() => {
    // El contenedor se busca una vez y se reutiliza; si el nodo se desmontó
    // (cambio de ruta), se vuelve a buscar. Antes se hacía querySelector en
    // cada evento de scroll.
    let contenedor = null;
    const guardar = () => {
      if (!contenedor || !contenedor.isConnected) {
        contenedor = document.querySelector('.dashboard-content');
      }
      posiciones.current.set(rutaActual.current, {
        contenedor: contenedor ? contenedor.scrollTop : 0,
        ventana: window.scrollY || 0
      });
    };

    window.addEventListener('scroll', guardar, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', guardar, { capture: true });
  }, []);

  // El navegador también intenta restaurar el scroll por su cuenta al volver.
  // Se desactiva para que no compita con esta restauración.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Al cerrar sesión se descartan: eran posiciones de la sesión anterior.
  useEffect(() => {
    if (!user) posiciones.current.clear();
  }, [user]);

  useLayoutEffect(() => {
    rutaActual.current = pathname;

    const contenedor = document.querySelector('.dashboard-content');
    const volviendo = navigationType === 'POP' || state?.restoreScroll === true;
    const guardada = volviendo ? posiciones.current.get(pathname) : null;

    const topContenedor = guardada ? guardada.contenedor : 0;
    const topVentana = guardada ? guardada.ventana : 0;

    if (contenedor) contenedor.scrollTop = topContenedor;
    window.scrollTo(0, topVentana);

    // Reintento en el siguiente frame: si la lista aún no había crecido cuando
    // se restauró, el primer intento queda recortado por el navegador.
    if (topContenedor > 0) {
      requestAnimationFrame(() => {
        const c = document.querySelector('.dashboard-content');
        if (c) c.scrollTop = topContenedor;
      });
    }
  }, [pathname, navigationType, state]);

  return null;
}

// Helper para lazy loading con reintentos automáticos
const lazyWithRetry = (componentImport) =>
  lazy(() =>
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
        return component;
      })
      .catch((error) => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
          window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );
        if (!pageHasAlreadyBeenForceRefreshed) {
          window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
          window.location.reload();
          return new Promise(() => { });
        }
        throw error;
      })
  );

// Lazy loading de páginas
const Login = lazyWithRetry(() => import('./pages/Login'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const InstitutionalClasses = lazyWithRetry(() => import('./pages/InstitutionalClasses'));
const InstitutionalClassDetail = lazyWithRetry(() => import('./pages/InstitutionalClassDetail'));
const StudentDetail = lazyWithRetry(() => import('./pages/StudentDetail'));
const AnecdotarioForm = lazyWithRetry(() => import('./pages/AnecdotarioForm'));
const AttendanceHistory = lazyWithRetry(() => import('./pages/AttendanceHistory'));
const AttendanceForm = lazyWithRetry(() => import('./pages/AttendanceForm'));
const AttendanceReport = lazyWithRetry(() => import('./pages/AttendanceReport'));
const ClassNotes = lazyWithRetry(() => import('./pages/ClassNotes'));
const AttendanceReportPending = lazyWithRetry(() => import('./pages/AttendanceReportPending'));
const PersonalClasses = lazyWithRetry(() => import('./pages/PersonalClasses'));
const PersonalClassDetail = lazyWithRetry(() => import('./pages/PersonalClassDetail'));
const PersonalClassForm = lazyWithRetry(() => import('./pages/PersonalClassForm'));
const Institution = lazyWithRetry(() => import('./pages/Institution'));
const Timetable = lazyWithRetry(() => import('./pages/Timetable'));
const GestionDocentes = lazyWithRetry(() => import('./pages/GestionDocentes'));
const StudentAttendanceHistory = lazyWithRetry(() => import('./pages/StudentAttendanceHistory'));
const PerfilDocente = lazyWithRetry(() => import('./pages/PerfilDocente'));
const InstitutionalReports = lazyWithRetry(() => import('./pages/InstitutionalReports'));
const ResumenGeneralSecciones = lazyWithRetry(() => import('./pages/ResumenGeneralSecciones'));
const TimeSlotAdmin = lazyWithRetry(() => import('./pages/TimeSlotAdmin'));
const Inicio = lazyWithRetry(() => import('./pages/Inicio'));
const StudentInstitutionalSearch = lazyWithRetry(() => import('./pages/StudentInstitutionalSearch'));
const Audit = lazyWithRetry(() => import('./pages/Audit'));
const AttendanceMasterCorrection = lazyWithRetry(() => import('./pages/AttendanceMasterCorrection'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Calendario = lazyWithRetry(() => import('./pages/Calendario'));
const CalendarAdminPage = lazyWithRetry(() => import('./pages/CalendarAdminPage'));
const HelpModule = lazyWithRetry(() => import('./pages/HelpModule'));
const Avisos = lazyWithRetry(() => import('./pages/Avisos'));

const PageLoader = () => (
  <div style={{ backgroundColor: 'var(--color-bg)', minHeight: '100dvh', position: 'relative' }}>
    <LoadingOverlay visible={true} />
  </div>
);

const RouteLoader = () => (
  <div style={{ backgroundColor: 'var(--color-bg)', minHeight: '100dvh', position: 'relative' }}>
    <LoadingOverlay visible={true} />
  </div>
);

function Forbidden() {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-secondary)' }}>
      <h2 style={{ color: 'var(--color-text)', marginBottom: 8 }}>Sin acceso</h2>
      <p style={{ margin: 0 }}>Tu perfil no tiene permisos para ver esta sección.</p>
    </div>
  );
}

/**
 * Exige sesión iniciada y, opcionalmente, uno de los roles indicados.
 *
 * Ocultar módulos del menú lateral no es control de acceso: sin esto,
 * cualquier usuario autenticado entra escribiendo la URL. Las reglas de
 * Firestore siguen siendo la barrera real de los datos.
 */
function PrivateRoute({ children, allowedRoles = null }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Forbidden />;
  return children;
}

// Matrices de roles para las rutas sensibles, alineadas con sidebarModules.js
// y con firestore.rules.
const SUPERADMIN = ['superadmin'];
const ADMIN = ['admin', 'superadmin'];
const ADMIN_O_GESTION = ['admin', 'superadmin', 'directivo', 'administrativo'];
const AVISOS = ['admin', 'superadmin', 'directivo'];

export default function App() {
  const [updateModal, setUpdateModal] = React.useState({ open: false, registration: null });
  const location = useLocation();

  // El fundido del área de la app sirve al pasar de Login a la app, pero en el
  // primer pintado solo retrasa la sensación de arranque.
  const esPrimerPintado = React.useRef(true);
  React.useEffect(() => {
    esPrimerPintado.current = false;
  }, []);

  React.useEffect(() => {
    const handleUpdate = (e) => {
      setUpdateModal({ open: true, registration: e.detail });
    };
    window.addEventListener('pwa-update-available', handleUpdate);
    return () => window.removeEventListener('pwa-update-available', handleUpdate);
  }, []);

  const handleUpdateApp = () => {
    setUpdateModal({ open: false, registration: null });
    const registration = updateModal.registration;
    if (registration && registration.waiting) {
      sessionStorage.setItem('pwa_updating', 'true');
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
      setTimeout(() => {
        if (sessionStorage.getItem('pwa_updating')) {
          window.location.reload();
        }
      }, 2000);
    } else {
      window.location.reload();
    }
  };

  // Clave que solo cambia entre Login y el App principal para evitar re-animar todo en sub-rutas aquí
  const authAreaKey = location.pathname.startsWith('/login') ? 'auth' : 'app';

  return (
    <InstitutionProvider>
      <ThemeHandler />
      <GlobalBackground />
      <ScrollManager />
      <FloatingBackButton />

      <AnimatePresence mode="wait">
        <motion.div
          key={authAreaKey}
          initial={esPrimerPintado.current ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{ minHeight: '100dvh', width: '100%' }}
        >
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>}>
                <Route index element={<Inicio />} />

                {/* Clases institucionales */}
                <Route path="classes/institucional" element={<InstitutionalClasses />} />
                <Route path="classes/institucional/:classId" element={<InstitutionalClassDetail />} />

                {/* Detalle de alumno institucional y anécdotas */}
                <Route path="classes/institucional/:classId/student/:studentId" element={<StudentDetail type="institutional" />}>
                  <Route path="anecdota" element={<AnecdotarioForm type="institutional" />} />
                </Route>
                <Route path="classes/institucional/:classId/student/:studentId/attendance-history" element={<StudentAttendanceHistory type="institutional" />} />

                {/* Asistencia institucional */}
                <Route path="attendance/master-correction" element={<PrivateRoute allowedRoles={ADMIN}><AttendanceMasterCorrection /></PrivateRoute>} />
                <Route path="attendance/institucional/:classId" element={<AttendanceHistory type="institutional" />} />
                <Route path="attendance/institucional/:classId/new" element={<AttendanceForm type="institutional" mode="new" />} />
                <Route path="attendance/institucional/:classId/edit/:recordId" element={<AttendanceForm type="institutional" mode="edit" />} />
                <Route path="attendance/institucional/:classId/copy/:recordId" element={<AttendanceForm type="institutional" mode="copy" />} />
                <Route path="attendance/institucional/:classId/edit-pending/:createdAt" element={<AttendanceForm type="institutional" mode="edit-pending" />} />
                <Route path="attendance/institucional/:classId/report/:recordId" element={<AttendanceReport type="institutional" mode="record" />} />
                <Route path="attendance/institucional/:classId/report-pending/:createdAt" element={<AttendanceReportPending type="institutional" />} />
                <Route path="attendance/institucional/:classId/reportes" element={<AttendanceReport type="institutional" mode="range" />} />
                <Route path="classes/institucional/:classId/notas" element={<ClassNotes type="institutional" />} />

                {/* Clases personales */}
                <Route path="classes/personal" element={<PersonalClasses />} />
                <Route path="classes/personal/:classId" element={<PersonalClassDetail />} />
                <Route path="classes/personal/:classId/edit" element={<PersonalClassForm />} />
                <Route path="classes/personal/:classId/student/:studentId" element={<StudentDetail type="personal" />}>
                  <Route path="anecdota" element={<AnecdotarioForm type="personal" />} />
                </Route>
                <Route path="classes/personal/:classId/student/:studentId/attendance-history" element={<StudentAttendanceHistory type="personal" />} />

                {/* Asistencia personal */}
                <Route path="attendance/personal/:classId" element={<AttendanceHistory type="personal" />} />
                <Route path="attendance/personal/:classId/new" element={<AttendanceForm type="personal" mode="new" />} />
                <Route path="attendance/personal/:classId/edit/:recordId" element={<AttendanceForm type="personal" mode="edit" />} />
                <Route path="attendance/personal/:classId/copy/:recordId" element={<AttendanceForm type="personal" mode="copy" />} />
                <Route path="attendance/personal/:classId/edit-pending/:createdAt" element={<AttendanceForm type="personal" mode="edit-pending" />} />
                <Route path="attendance/personal/:classId/report/:recordId" element={<AttendanceReport type="personal" mode="record" />} />
                <Route path="attendance/personal/:classId/report-pending/:createdAt" element={<AttendanceReportPending type="personal" />} />
                <Route path="attendance/personal/:classId/reportes" element={<AttendanceReport type="personal" mode="range" />} />
                <Route path="classes/personal/:classId/notas" element={<ClassNotes type="personal" />} />

                {/* Institución (admin) */}
                <Route path="institution" element={<PrivateRoute allowedRoles={ADMIN}><Institution /></PrivateRoute>} />

                {/* Horario / Franjas */}
                <Route path="timetable" element={<Timetable />} />
                <Route path="timeslots" element={<PrivateRoute allowedRoles={ADMIN}><TimeSlotAdmin /></PrivateRoute>} />

                {/* Auditoría de Sistema */}
                <Route path="audit" element={<PrivateRoute allowedRoles={ADMIN_O_GESTION}><Audit /></PrivateRoute>} />

                {/* Configuración (Super Admin) */}
                <Route path="settings" element={<PrivateRoute allowedRoles={SUPERADMIN}><Settings /></PrivateRoute>} />


                {/* Gestión de docentes */}
                <Route path="docentes" element={<PrivateRoute allowedRoles={ADMIN}><GestionDocentes /></PrivateRoute>} />

                {/* Perfil Docente */}
                <Route path="perfil" element={<PerfilDocente />} />

                {/* Reportes Institucionales */}
                <Route path="reportes-institucionales" element={<PrivateRoute allowedRoles={ADMIN_O_GESTION}><InstitutionalReports /></PrivateRoute>} />

                {/* Resumen general de secciones */}
                <Route path="asistencia/resumen" element={<ResumenGeneralSecciones />} />

                {/* Buscar por NIE */}
                <Route path="estudiantes-institucionales" element={<StudentInstitutionalSearch />} />

                <Route path="calendario" element={<Calendario />} />
                <Route path="calendario/admin" element={<PrivateRoute allowedRoles={ADMIN_O_GESTION}><CalendarAdminPage /></PrivateRoute>} />
                <Route path="ayuda" element={<HelpModule />} />
                <Route path="avisos" element={<PrivateRoute allowedRoles={AVISOS}><Avisos /></PrivateRoute>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </motion.div>
      </AnimatePresence>

      <Modal
        isOpen={updateModal.open}
        title="Actualización disponible"
        message="Hay una nueva versión de la aplicación con mejoras y correcciones. ¿Deseas actualizar ahora?"
        confirmText="Actualizar ahora"
        cancelText="Más tarde"
        onConfirm={handleUpdateApp}
        onCancel={() => setUpdateModal({ open: false, registration: null })}
      />
    </InstitutionProvider>
  );
}